import type { PlaceCategory, TravelMode } from '../../lib/api/types';
import { usesMiles } from '../../lib/stores/units.svelte';
import { decimal, integer, locale, t } from '../../lib/i18n/index.ts';

// Formatting shared by the timeline's tabs.

const categoryIcons: Record<PlaceCategory, string> = {
  food_drink: 'la-utensils',
  shopping: 'la-shopping-bag',
  attractions: 'la-camera',
  culture: 'la-landmark',
  lodging: 'la-bed',
  work_education: 'la-briefcase',
  health: 'la-hospital',
  outdoors: 'la-tree',
  transport: 'la-bus',
  home: 'la-home',
  other: 'la-map-marker',
};

export function categoryIcon(c: PlaceCategory | undefined): string {
  return categoryIcons[c ?? 'other'];
}

const modeIcons: Record<TravelMode, string> = {
  walking: 'la-walking',
  running: 'la-running',
  cycling: 'la-bicycle',
  driving: 'la-car',
  bus: 'la-bus',
  train: 'la-train',
  flight: 'la-plane',
  other: 'la-route',
};

export function modeIcon(m: TravelMode): string {
  return modeIcons[m] ?? 'la-route';
}

export function modeLabel(m: TravelMode): string {
  return t('timelineMode', { mode: m });
}

export function duration(start: string, end: string): string {
  return minutesText(Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000));
}

// minutesText writes a number of minutes as minutes, or hours and minutes
// from an hour on.
function minutesText(mins: number): string {
  if (mins < 60) return t('timelineMinutes', { n: mins });
  const h = Math.floor(mins / 60);
  return mins % 60 ? t('timelineHoursMinutes', { hours: integer(h), minutes: integer(mins % 60) }) : t('timelineHours', { n: h });
}

export function distance(m: number): string {
  const miles = usesMiles();
  const v = miles ? m / 1609.344 : m / 1000;
  const n = v < 10 ? decimal(v, 1) : integer(Math.round(v));
  return miles ? t('timelineDistanceMi', { distance: n }) : t('timelineDistanceKm', { distance: n });
}

export function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const categories: PlaceCategory[] = [
  'food_drink', 'shopping', 'attractions', 'culture', 'lodging', 'work_education', 'health', 'outdoors', 'transport', 'home', 'other',
];

export function categoryLabel(c: PlaceCategory): string {
  return t('timelineCategory', { category: c });
}

// span says how long a number of seconds is, in hours past the first hour.
export function span(seconds: number): string {
  return minutesText(Math.round(seconds / 60));
}

// hourName is an hour of the day on the region's clock, "9 AM" or "09".
export function hourName(h: number): string {
  return new Intl.DateTimeFormat(locale(), { hour: 'numeric' }).format(new Date(2024, 0, 1, h)).replace(/\u202f/g, ' ');
}

// shortDate is a day without its time, with the year only when not this one.
export function shortDate(iso: string): string {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(locale(), opts);
}

// countryName is a country's name in the viewer's language, from its code.
export function countryName(code: string | undefined, fallback?: string): string {
  if (!code) return fallback || t('timelineUnknownCountry');
  try {
    return new Intl.DisplayNames([locale()], { type: 'region' }).of(code.toUpperCase()) ?? fallback ?? code;
  } catch {
    return fallback || code;
  }
}

// timelineBase is the API path of a timeline: the caller's own or a member's through a group.
export function timelineBase(groupId: string | undefined, userId: string, self: boolean): string {
  return self || !groupId ? '/me' : `/groups/${groupId}/members/${userId}`;
}

// modeColors are each way of travel's color in charts and on maps.
export const modeColors: Record<TravelMode, string> = {
  walking: 'var(--theme-green)', running: 'var(--theme-yellow)', cycling: 'var(--theme-teal)', driving: 'var(--theme-blue)',
  bus: 'var(--theme-peach)', train: 'var(--theme-sapphire)', flight: 'var(--theme-pink)', other: 'var(--theme-mauve)',
};

// categoryColors are each place category's color in the day's ring, a
// fixed hue per category so a category keeps its color from day to day.
export const categoryColors: Record<PlaceCategory, string> = {
  home: 'var(--theme-green)', work_education: 'var(--theme-blue)', food_drink: 'var(--theme-peach)', shopping: 'var(--theme-pink)',
  attractions: 'var(--theme-yellow)', culture: 'var(--theme-mauve)', lodging: 'var(--theme-lavender)', health: 'var(--theme-red)',
  outdoors: 'var(--theme-teal)', transport: 'var(--theme-sapphire)', other: 'var(--theme-overlay2)',
};

// change is a total's change from the period before, "+18%" or "−4%";
// empty when there is nothing to compare with.
export function change(now: number, before: number): string {
  if (!before) return '';
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return t('timelineChangeSame');
  return pct > 0 ? t('timelineChangeUp', { percent: integer(pct) }) : t('timelineChangeDown', { percent: integer(-pct) });
}

// dayName is a YYYY-MM-DD day in full, "Monday, September 28, 2026".
export function dayName(d: string): string {
  return new Date(`${d}T12:00:00`).toLocaleDateString(locale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// dayShort is a YYYY-MM-DD day in short, "Mon, Sep 28, 2026".
export function dayShort(d: string): string {
  return new Date(`${d}T12:00:00`).toLocaleDateString(locale(), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// placeName is what a place is called: its name, else its address.
export function placeName(p: { name?: string; address?: string }): string {
  return p.name || p.address || t('timelineUnnamedPlace');
}

// NOTE_MAX is the longest note, in characters (005 FR-042).
export const NOTE_MAX = 2000;
